import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bell, Send } from 'lucide-react';
import { directApiInstance } from '../../api/httpClient';
import { useDeviceTokenUsers } from '../../hooks/useDeviceTokens';
import { useResolveAudience, EMPTY_AUDIENCE_FILTER, isAudienceFilterEmpty, type AudienceFilter } from '../../hooks/usePushAudience';
import { PushAudienceBuilder, type SelectedUser } from './PushAudienceBuilder';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { TableContent } from '../LayoutPage';
import { toaster } from '../../components/ui/toast';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';

// This is a new file — per the SAM-175 BaseUI/Styletron freeze
// (SAM-175), new code may not import baseui or
// styletron-react outside src/components/ui/. Built with Tailwind +
// tokens.css + src/components/ui/* wrappers, matching the pattern
// PushAudienceBuilder.tsx (SAM-264) already established for this same
// feature.
//
// SAM-376: this replaces the narrow side drawer that used to live inline
// in PushNotifications.tsx with a full-page compose view. The state,
// hooks, validation and submit call below are moved over verbatim from
// that drawer — this is a layout change only, the payload sent to the
// backend is unchanged.

// Sends above this many real recipients (users with an active device token)
// require an explicit confirmation before dispatching (SAM-264 safeguard).
const LARGE_SEND_CONFIRM_THRESHOLD = 100;

const NOTIFICATION_TYPES = [
  { id: 'general', label: 'General' },
  { id: 'booking_confirmed', label: 'Booking Confirmed' },
  { id: 'booking_cancelled', label: 'Booking Cancelled' },
  { id: 'promo_code', label: 'Promo Code' },
  { id: 'event', label: 'Event' },
  { id: 'announcement', label: 'Announcement' },
];

export const PushNotificationCompose: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uniqueUsers } = useDeviceTokenUsers();

  // Form state — same shape/state as the old drawer.
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notifType, setNotifType] = useState<Array<{ id: string; label: string }>>([{ id: 'general', label: 'General' }]);
  const [sendToAll, setSendToAll] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Audience — kept as a plain serializable filter object (see usePushAudience)
  // so armar-audiencia and enviar stay decoupled; SAM-265 will persist this
  // same shape as a named segment instead of re-deriving it from component state.
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>(EMPTY_AUDIENCE_FILTER);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);

  const audience = useResolveAudience(audienceFilter);
  const audienceHasSelection = !isAudienceFilterEmpty(audienceFilter);

  const goBackToList = () => navigate('/settings/push-notifications');

  const dispatchSend = async () => {
    setSending(true);
    try {
      const endpoint = sendToAll ? '/notifications/send-to-all' : '/notifications/send';
      const payload: any = { title: title.trim(), description: description.trim(), type: notifType[0]?.id || 'general', send_push: true };
      if (!sendToAll) payload.user_ids = audience.reachableUserIds;

      const response = await directApiInstance({ url: endpoint, method: 'POST', data: payload }) as any;
      const data = response?.data || response;
      const sent = data?.push_sent || data?.sent || 0;
      const usersNotified = data?.users_notified || audience.reachableCount || 'all';

      toaster.positive(`Notification sent to ${usersNotified} user(s) (${sent} push delivered)`, { autoHideDuration: 3000 });
      queryClient.invalidateQueries({ queryKey: ['sent-notifications'] });
      goBackToList();
    } catch (err: any) {
      toaster.negative(err?.response?.data?.message || err?.message || 'Failed to send', { autoHideDuration: 3000 });
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!title.trim()) { toaster.negative('Title is required', { autoHideDuration: 3000 }); return; }
    if (!sendToAll && !audienceHasSelection) { toaster.negative('Pick a segment or at least one user', { autoHideDuration: 3000 }); return; }
    if (!sendToAll && audience.reachableCount === 0) {
      toaster.negative('No recipients with an active device token match this audience', { autoHideDuration: 3500 });
      return;
    }

    if (!sendToAll && audience.reachableCount > LARGE_SEND_CONFIRM_THRESHOLD) {
      setConfirmOpen(true);
      return;
    }

    await dispatchSend();
  };

  const sendDisabled = sending || !title.trim() || (!sendToAll && (!audienceHasSelection || audience.isLoading));
  const sendLabel = sending ? 'Sending...' : sendToAll ? `Send to all (${uniqueUsers.length})` : `Send to ${audience.reachableCount} user${audience.reachableCount !== 1 ? 's' : ''}`;

  return (
    <TableContent>
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-line px-4 py-4 sm:px-6">
          <Button variant="ghost" onClick={goBackToList} aria-label="Back to notifications" className="!px-2.5">
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-[18px] font-semibold text-ink-900">New notification</h1>
            <p className="truncate text-[13px] text-ink-500">Compose a push notification and choose who receives it.</p>
          </div>
        </div>

        {/* Body — two zones, stacked on mobile */}
        <div className="flex-1 overflow-auto px-4 py-6 sm:px-6">
          <div className="mx-auto grid w-full max-w-[1180px] grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
            {/* Message composition */}
            <Card title="Message">
              <div className="fc">
                <label>Title <span className="req">*</span></label>
                <Input
                  value={title}
                  onChange={e => setTitle(e.currentTarget.value)}
                  placeholder="Notification title"
                  maxLength={120}
                />
              </div>

              <div className="fc">
                <label>Message</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.currentTarget.value)}
                  placeholder="Notification message body"
                  rows={4}
                />
              </div>

              <div className="fc">
                <label>Type</label>
                <select
                  value={notifType[0]?.id || 'general'}
                  onChange={e => {
                    const opt = NOTIFICATION_TYPES.find(t => t.id === e.target.value);
                    setNotifType(opt ? [opt] : []);
                  }}
                >
                  {NOTIFICATION_TYPES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-[13px] font-semibold text-ink-700">Preview</label>
                <div className="flex items-start gap-2.5 rounded-10 border border-line bg-surface p-3">
                  <div
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-8"
                    style={{ background: 'color-mix(in srgb, var(--tenant-primary) 9%, white)', color: 'var(--tenant-primary)' }}
                  >
                    <Bell size={15} />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-semibold text-ink-900">{title.trim() || 'Notification title'}</div>
                    <div className="line-clamp-2 text-[13px] text-ink-500">{description.trim() || 'Your message will appear here.'}</div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Audience selection */}
            <Card title="Audience">
              <div className="mb-4">
                <Checkbox checked={sendToAll} onChange={e => setSendToAll(e.currentTarget.checked)}>
                  <span className="text-[14px] font-medium text-ink-900">Send to all users ({uniqueUsers.length})</span>
                </Checkbox>
              </div>

              {!sendToAll && (
                <>
                  <PushAudienceBuilder
                    filter={audienceFilter}
                    onFilterChange={setAudienceFilter}
                    selectedUsers={selectedUsers}
                    onSelectedUsersChange={setSelectedUsers}
                  />

                  <div className="mt-3 rounded-8 bg-surface-2 p-3 text-[13px] text-ink-700">
                    {audience.isLoading ? (
                      <span>Calculating recipients…</span>
                    ) : (
                      <>
                        <strong>{audience.reachableCount}</strong> recipient{audience.reachableCount !== 1 ? 's' : ''} with an active device token
                        {audience.matchedCount > audience.reachableCount && (
                          <span> (of {audience.matchedCount} matched — the rest have no active device token)</span>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-line bg-white px-4 py-4 sm:px-6">
          <div className="mx-auto flex max-w-[1180px] flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
            <Button variant="ghost" onClick={goBackToList} disabled={sending}>Cancel</Button>
            <Button variant="primary" onClick={handleSend} disabled={sendDisabled} icon={<Send size={15} />}>
              {sendLabel}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmOpen}
        title="Confirm large send"
        message={`You're about to send this notification to ${audience.reachableCount} people. This can't be undone. Continue?`}
        confirmText="Send"
        cancelText="Cancel"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => { setConfirmOpen(false); dispatchSend(); }}
      />
    </TableContent>
  );
};
